import SyncIcon from "@mui/icons-material/Sync";
import React from "react";
import { Tooltip } from "@mui/material";
import { NavigateFunction } from "react-router-dom";
import { useSyncSiteFlow } from "./useSyncSiteFlow";

/**
 * Lightweight, self-contained Sync control for the edit-view headers.
 *
 * Saving a settings change no longer auto-syncs — it marks the site unsynced
 * in the shared sync store. This control appears once that happens and lets
 * the operator sync deliberately; the sync + reboot-prompt behavior lives in
 * the shared useSyncSiteFlow hook (also used by SyncNeededBanner).
 */

export const EditViewSyncButton = ({
  navigate,
}: {
  navigate: NavigateFunction;
}) => {
  const { syncNeeded, syncing, syncApplies, syncNow, rebootDialog } =
    useSyncSiteFlow(navigate);

  if (!syncApplies) {
    return null;
  }

  let button: JSX.Element | null = null;
  if (syncing) {
    button = (
      <button
        className="shadow-button bg-[#ed6c02] rounded font-mulish text-white text-base p-1.5 cursor-wait"
        disabled={true}
      >
        <SyncIcon fontSize="small" className="animate-spin" />
        &nbsp; Syncing... &nbsp;
      </button>
    );
  } else if (syncNeeded) {
    // While editing there is nothing to sync yet, so the button is hidden. It
    // appears only once a submit has marked the site unsynced.
    button = (
      <Tooltip
        title="You have unsynced changes. Sync to push them to the cloud, then reboot the site to apply them."
        placement="top"
        sx={{ fontSize: "15px", fontFamily: "mulish" }}
      >
        <button
          className="shadow-button bg-[#d32f2f] rounded font-mulish text-white text-base p-1.5 hover:bg-[#c62828]"
          onClick={syncNow}
          disabled={false}
        >
          <SyncIcon fontSize="small" />
          &nbsp; Sync Site &nbsp;
        </button>
      </Tooltip>
    );
  }

  return (
    <>
      {rebootDialog}
      {button}
    </>
  );
};
